import { Link } from 'react-router-dom';
import {
  FileText,
  ClipboardList,
  LifeBuoy,
  MessageSquare,
  AlertTriangle,
  AlertOctagon,
  Plus,
} from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { useJSAs } from '@/hooks/use-safety';
import { useSafeWorkPlans } from '@/hooks/use-safety';
import { useNearMisses } from '@/hooks/use-safety';
import { useToolboxTalks } from '@/hooks/use-safety';
import { useIncidentReports } from '@/hooks/use-safety';
import { useRescueDrills } from '@/hooks/use-safety';
import { usePermissions } from '@/hooks/use-permissions';
import { SafetyMetricsSection } from './safety-metrics-section';

export function SafetyDashboardPage() {
  const { hasMinModuleRole } = usePermissions();
  const canManageSafety = hasMinModuleRole('safety', 'manager');
  const { data: jsaData } = useJSAs({ page_size: 1, status: 'active' });
  const { data: swpData } = useSafeWorkPlans({ page_size: 1, status: 'approved' });
  const { data: nearMissData } = useNearMisses({ page_size: 1, status: 'reported' });
  const { data: talksData } = useToolboxTalks({ page_size: 1 });
  const { data: incidentData } = useIncidentReports({ page_size: 1, status: 'reported' });
  const { data: drillData } = useRescueDrills({ page_size: 1 });

  const summaryCards = [
    {
      title: 'Active JSAs',
      value: jsaData?.count ?? '-',
      icon: FileText,
      color: 'text-blue-600',
      bg: 'bg-blue-50',
      link: '/safety/jsas?status=active',
    },
    {
      title: 'Approved Work Plans',
      value: swpData?.count ?? '-',
      icon: ClipboardList,
      color: 'text-green-600',
      bg: 'bg-green-50',
      link: '/safety/work-plans?status=approved',
    },
    {
      title: 'Open Near-Misses',
      value: nearMissData?.count ?? '-',
      icon: AlertTriangle,
      color: 'text-orange-600',
      bg: 'bg-orange-50',
      link: '/safety/near-misses?status=reported',
    },
    {
      title: 'Open Incidents',
      value: incidentData?.count ?? '-',
      icon: AlertOctagon,
      color: 'text-red-600',
      bg: 'bg-red-50',
      link: '/safety/incidents?status=reported',
    },
    {
      title: 'Toolbox Talks',
      value: talksData?.count ?? '-',
      icon: MessageSquare,
      color: 'text-purple-600',
      bg: 'bg-purple-50',
      link: '/safety/toolbox-talks',
    },
    {
      title: 'Rescue Drills',
      value: drillData?.count ?? '-',
      icon: LifeBuoy,
      color: 'text-cyan-600',
      bg: 'bg-cyan-50',
      link: '/safety/rescue-drills',
    },
  ];

  const quickActions = [
    ...(canManageSafety
      ? [
          { label: 'New JSA', icon: FileText, to: '/safety/jsas/new' },
          { label: 'New Work Plan', icon: ClipboardList, to: '/safety/work-plans/new' },
          { label: 'New Rescue Plan', icon: LifeBuoy, to: '/safety/rescue-plans/new' },
          { label: 'Toolbox Talk', icon: MessageSquare, to: '/safety/toolbox-talks/new' },
        ]
      : []),
    { label: 'Report Near-Miss', icon: AlertTriangle, to: '/safety/near-misses/new' },
    { label: 'Report Incident', icon: AlertOctagon, to: '/safety/incidents/new' },
    ...(canManageSafety
      ? [{ label: 'Log Drill', icon: LifeBuoy, to: '/safety/rescue-drills/new' }]
      : []),
  ];

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1><img src="/logos/h3safe.svg" alt="H3Safe" className="h-7" /></h1>
          <p className="text-gray-600 mt-1">Safety &amp; compliance management</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" />
            Quick Actions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
            {quickActions.map((action) => (
              <Button
                key={action.label}
                variant="outline"
                className="h-auto py-4 flex flex-col items-center gap-2"
                asChild
              >
                <Link to={action.to}>
                  <action.icon className="h-5 w-5" />
                  <span className="text-sm">{action.label}</span>
                </Link>
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {summaryCards.map((card) => (
          <Link key={card.title} to={card.link}>
            <Card className="hover:shadow-md transition-shadow cursor-pointer">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">{card.title}</p>
                    <p className="text-2xl font-bold mt-1">{card.value}</p>
                  </div>
                  <div className={`p-3 rounded-lg ${card.bg}`}>
                    <card.icon className={`h-6 w-6 ${card.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      <SafetyMetricsSection />
    </div>
  );
}
